(ns app.front.ui.key-value
  "Key-value display for read-only details, e.g. an entity page's details card,
  rendered as a description list so the markup carries the label/value
  relationship."
  (:require [app.front.util :refer [for-frag]]
            [clojure.string :as str]
            [potpuri.core :as p]))

(def missing-value
  "Shown in place of a value that is nil or blank."
  "—")

(defn- blank-value? [value]
  (or (nil? value)
      (and (string? value) (str/blank? value))))

(defn key-value
  "A single label/value pair: a small-caps label above its value.

  Renders a `<dt>`/`<dd>` pair, so it belongs inside `key-value-list` (or any
  other `<dl>`).

  Props:
  - `:label`   label content, string or hiccup
  - `:value`   value content, string or hiccup. `missing-value` is shown when
               nil or blank
  - `:class`   extra classes for the value element
  - `:data-cy` test hook for the value element"
  [{:keys [label value class data-cy]}]
  [:div
   [:dt.text-xs.uppercase.tracking-wide.font-semibold.text-gray-700
    label]
   [:dd.mt-2.ml-0.text-ui-night-sky-100
    (p/assoc-if {:class class} :data-cy data-cy)
    (if (blank-value? value)
      missing-value
      value)]])

(defn key-value-list
  "Container for `key-value` items: a `<dl>` laid out as a responsive grid that
  collapses to a single column on small screens.

  Props:
  - `:columns` column count from the `sm` breakpoint up, 1-3 (default 2)
  - `:class`   extra classes for the list element"
  [{:keys [columns class]} & children]
  (into [:dl.grid.gap-x-6.gap-y-5.m-0
         {:class [(case columns
                    1 nil
                    3 "sm:grid-cols-3"
                    "sm:grid-cols-2")
                  class]}]
        children))

(defn key-values
  "A `key-value-list` of plain data: one `key-value` per entry of `:values`.

  Props:
  - `:values`  sequence of `key-value` prop maps
  - `:columns` and `:class` as in `key-value-list`"
  [{:keys [values] :as props}]
  [key-value-list (dissoc props :values)
   (for-frag [value values]
     [key-value value])])
